import { useState, useEffect } from 'react';
import { Modal, Select, Switch, Button, Space, Divider, Tag, Typography, DatePicker, message } from 'antd';
import dayjs from 'dayjs';
import { ExportOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { EntityAvatar } from './EntityAvatar';
import { useApi } from '../../hooks/use_api';

const { Text } = Typography;

function ConnectionEditModal({ open, onClose, conn, currentEntityId, isAdmin, canAcceptConnection = false, userId, onSuccess }) {
    const navigate = useNavigate();
    const api = useApi();
    const [saving, setSaving] = useState(false);
    const [myDescriptions, setMyDescriptions] = useState([]);   // all descriptions for current entity
    const [theirDescription, setTheirDescription] = useState(null); // the single linked description for their side
    const [selectedMyDescId, setSelectedMyDescId] = useState(null);
    const [connectedStatus, setConnectedStatus] = useState(false);
    const [isAdminRole, setIsAdminRole] = useState(false);
    const [windowRange, setWindowRange] = useState([null, null]);
    const [pauseUntil, setPauseUntil] = useState(null);
    const [pausing, setPausing] = useState(false);

    useEffect(() => {
        if (!open || !conn || !currentEntityId) return;

        // Derive which side I am from the stored entity_a/b_id
        const iAmA = conn.entity_a_id === currentEntityId;
        const myDescId   = iAmA ? conn.entity_a_description_id : conn.entity_b_description_id;
        const theirDescId = iAmA ? conn.entity_b_description_id : conn.entity_a_description_id;

        setSelectedMyDescId(myDescId ?? null);
        setConnectedStatus(conn.connected_status);
        setIsAdminRole(conn.role === 'admin');
        setWindowRange([conn.starts_at ? dayjs(conn.starts_at) : null, conn.ends_at ? dayjs(conn.ends_at) : null]);
        setPauseUntil(null);
        setTheirDescription(null);

        // 1. All descriptions for the current entity → populates the select options
        api.get(`/api/entities/${currentEntityId}/descriptions`)
            .then(setMyDescriptions)
            .catch(() => setMyDescriptions([]));

        // 2. The single description the other entity has linked → read-only display
        if (theirDescId) {
            api.get(`/api/entities/descriptions/${theirDescId}`)
                .then(setTheirDescription)
                .catch(() => setTheirDescription(null));
        }
    }, [open, conn, currentEntityId]);  // re-run whenever the modal opens or a different conn is selected

    if (!conn) return null;

    const iAmA = conn.entity_a_id === currentEntityId;
    const isInstall = conn.role === 'installed';
    const STATE_LABELS = { live: ['green', 'Live'], pending: ['orange', 'Pending'], paused: ['purple', 'Paused'], before: ['blue', 'Not started'], after: ['default', 'Ended'] };
    const [stateColor, stateLabel] = STATE_LABELS[conn.state] || STATE_LABELS[conn.connected_status ? 'live' : 'pending'];

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                // Only write our side; preserve their side unchanged
                entity_a_description_id: iAmA ? selectedMyDescId : conn.entity_a_description_id,
                entity_b_description_id: iAmA ? conn.entity_b_description_id : selectedMyDescId,
                // A term (todo D12): outside its window a connection counts as paused.
                starts_at: windowRange[0] ? windowRange[0].toISOString() : null,
                ends_at: windowRange[1] ? windowRange[1].toISOString() : null,
            };
            // An app's install keeps its role; it is never admin or member.
            if (!isInstall) payload.role = isAdminRole ? 'admin' : 'member';
            // Only send connected_status if the caller is allowed to flip it; this also
            // ensures self-accept attempts surface a clear backend error.
            if (canAcceptConnection) payload.connected_status = connectedStatus;

            await api.put(`/api/connections/${conn.connection_id}`, payload, {
                params: userId ? { user_id: userId } : undefined,
            });
            message.success('Connection updated');
            onSuccess?.();
            onClose();
        } catch (e) {
            message.error(e.message || 'Failed to update connection');
        } finally {
            setSaving(false);
        }
    };

    // Pause (todo D9): time-boxed, lifted by the end that paused it or a group admin.
    const pause = () => {
        setPausing(true);
        api.post(`/api/connections/${conn.connection_id}/pause`, { until: pauseUntil.toISOString() })
            .then(() => { message.success(`Paused until ${pauseUntil.format('D MMM YYYY')}`); onSuccess?.(); onClose(); })
            .catch(err => message.error(err.message || 'Could not pause'))
            .finally(() => setPausing(false));
    };
    const resume = () => {
        setPausing(true);
        api.post(`/api/connections/${conn.connection_id}/resume`)
            .then(() => { message.success('Resumed'); onSuccess?.(); onClose(); })
            .catch(err => message.error(err.message || 'Could not resume'))
            .finally(() => setPausing(false));
    };

    const myDescOptions = myDescriptions.map(d => ({
        value: d.id,
        label: d.is_default ? `${d.label} (default)` : d.label,
    }));

    return (
        <Modal
            title={
                <Space>
                    <span>Connection — <strong>{conn.entity.name}</strong></span>
                    {conn.role && <Tag>{conn.role}</Tag>}
                </Space>
            }
            open={open}
            onCancel={onClose}
            footer={
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Button icon={<ExportOutlined />} onClick={() => navigate(`/entity/${conn.entity.id}`)}>
                        Open entity
                    </Button>
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        {isAdmin && (
                            <Button type="primary" loading={saving} onClick={handleSave}>Save</Button>
                        )}
                    </Space>
                </Space>
            }
            width={480}
        >
            {/* Connection status */}
            {isAdmin ? (
                <Space direction="vertical" style={{ width: '100%', marginBottom: 20 }} size={12}>
                    {canAcceptConnection ? (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text strong>Connected</Text>
                            <Switch checked={connectedStatus} onChange={setConnectedStatus} />
                        </div>
                    ) : (
                        <div>
                            <Text type="secondary">Status: </Text>
                            <Tag color={conn.connected_status ? 'green' : 'orange'}>
                                {conn.connected_status ? 'Connected' : 'Pending'}
                            </Tag>
                        </div>
                    )}
                    {!isInstall && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text strong>Admin</Text>
                            <Switch checked={isAdminRole} onChange={setIsAdminRole} />
                        </div>
                    )}
                    <div>
                        <Space style={{ marginBottom: 6 }}>
                            <Text strong>Time</Text>
                            <Tag color={stateColor}>{stateLabel}</Tag>
                            {conn.state === 'paused' && conn.paused_until && <Text type="secondary">until {dayjs(conn.paused_until).format('D MMM YYYY')}</Text>}
                        </Space>
                        <DatePicker.RangePicker
                            style={{ width: '100%' }}
                            allowEmpty={[true, true]}
                            value={windowRange}
                            onChange={v => setWindowRange(v || [null, null])}
                            placeholder={['From (open)', 'Until (open)']}
                        />
                        {conn.connected_status && (
                            conn.state === 'paused' ? (
                                <Button style={{ marginTop: 8 }} loading={pausing} onClick={resume}>Resume now</Button>
                            ) : (
                                <Space style={{ marginTop: 8 }}>
                                    <DatePicker
                                        placeholder="Pause until"
                                        value={pauseUntil}
                                        onChange={setPauseUntil}
                                        disabledDate={d => d && (d.isBefore(dayjs(), 'day') || d.isAfter(dayjs().add(365, 'day')))}
                                    />
                                    <Button disabled={!pauseUntil} loading={pausing} onClick={pause}>Pause</Button>
                                </Space>
                            )
                        )}
                    </div>
                </Space>
            ) : (
                <div style={{ marginBottom: 20 }}>
                    <Text type="secondary">Status: </Text>
                    <Tag color={stateColor}>{stateLabel}</Tag>
                </div>
            )}

            <Divider style={{ margin: '12px 0' }} />

            {/* Current entity — editable select from all their descriptions */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <EntityAvatar entityId={currentEntityId} size={24} />
                    <Text strong>Description shared in this link</Text>
                </div>
                <Select
                    style={{ width: '100%' }}
                    placeholder={myDescOptions.length === 0 ? 'No descriptions added yet' : 'Select a description'}
                    allowClear
                    value={selectedMyDescId}
                    onChange={setSelectedMyDescId}
                    options={myDescOptions}
                    disabled={!isAdmin || myDescOptions.length === 0}
                />
            </div>

            {/* Connected entity — read-only, fetched by FK from entity_descriptions */}
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <EntityAvatar entityId={conn.entity.id} size={24} />
                    <Text strong>Their shared description</Text>
                </div>
                {theirDescription ? (
                    <div style={{
                        padding: '8px 12px',
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: 6,
                        fontSize: 13,
                    }}>
                        <Tag style={{ marginBottom: 4 }}>{theirDescription.label}</Tag>
                        <div style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
                            {theirDescription.content || <em>No content</em>}
                        </div>
                    </div>
                ) : (
                    <Text type="secondary" style={{ fontSize: 13 }}>Not set</Text>
                )}
            </div>
        </Modal>
    );
}

export default ConnectionEditModal;
